import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Repeat, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"
import { TemplateField } from "./template-field"

export function LoopNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { mode: value })
    },
    [id, updateNodeData]
  )

  const handleIterationsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value) || 1
      updateNodeData(id, { iterations: value })
    },
    [id, updateNodeData]
  )

  const handleMaxIterationsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value) || 10
      updateNodeData(id, { maxIterations: value })
    },
    [id, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const mode = (data.mode as string) || "count"
  const iterations = (data.iterations as number) || 3
  const maxIterations = (data.maxIterations as number) || 10
  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Loop Mode</Label>
        <Select value={mode} onValueChange={handleModeChange}>
          <SelectTrigger className="nodrag h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="count">Fixed count</SelectItem>
            <SelectItem value="array">Iterate array</SelectItem>
            <SelectItem value="while">While condition</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === "count" && (
        <div className="grid gap-1.5">
          <Label className="text-xs">Iterations</Label>
          <Input
            className="nodrag h-7 text-xs"
            min={1}
            type="number"
            value={iterations}
            onChange={handleIterationsChange}
          />
        </div>
      )}

      {mode === "while" && (
        <div className="grid gap-1.5">
          <Label className="text-xs">Continue While</Label>
          <TemplateField
            multiline
            className={cn("pr-8", isModal ? "min-h-[260px]" : "min-h-[50px]")}
            placeholder="The task is not complete..."
            value={(data.condition as string) || ""}
            onChange={(value) => updateNodeData(id, { condition: value })}
          />
        </div>
      )}

      <div className="grid gap-1.5">
        <Label className="text-xs">Max Iterations (safety)</Label>
        <Input
          className="nodrag h-7 text-xs"
          min={1}
          type="number"
          value={maxIterations}
          onChange={handleMaxIterationsChange}
        />
      </div>

      <div className="text-muted-foreground text-center text-[10px]">
        {mode === "count" && `Repeats ${iterations}x`}
        {mode === "array" && "Iterates each item"}
        {mode === "while" && "Continues while condition is true"}
      </div>
      <div className="grid gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!data.showOutput}
            className="nodrag"
            id={`${id}-showOutput${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowOutputChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
          >
            Show Output
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={data.showInActivityHistory !== false}
            className="nodrag"
            id={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowInActivityHistoryChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
          >
            Show in Activity History
          </Label>
        </div>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[200px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[3])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Repeat className="size-4" />
            Loop
            <NodeEditDialog
              description="Edit this loop node in a larger workspace."
              title="Loop settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
